import MapReveal from "@/assets/img/astro-map/map-reveal.png"
import Step12_1 from "@/assets/img/astro-map/step12-1.png"
import Step12_2 from "@/assets/img/astro-map/step12-2.png"
import Step12_3 from "@/assets/img/astro-map/step12-3.png"
import Step12_4 from "@/assets/img/astro-map/step12-4.png"
import Step12_5 from "@/assets/img/astro-map/step12-5.png"
import Step12_6 from "@/assets/img/astro-map/step12-6.png"
import Step19_1 from "@/assets/img/astro-map/step19-1.png"
import Step19_2 from "@/assets/img/astro-map/step19-2.png"
import Step19_3 from "@/assets/img/astro-map/step19-3.png"
import Step19_4 from "@/assets/img/astro-map/step19-4.png"
import Step19_5 from "@/assets/img/astro-map/step19-5.png"
import Step4_1 from "@/assets/img/astro-map/step4-1.png"
import Step4_2 from "@/assets/img/astro-map/step4-2.png"
import Step4_3 from "@/assets/img/astro-map/step4-3.png"
import Step4_4 from "@/assets/img/astro-map/step4-4.png"
import Step5_1 from "@/assets/img/astro-map/step5-1.png"
import Step5_2 from "@/assets/img/astro-map/step5-2.png"
import Step5_3 from "@/assets/img/astro-map/step5-3.png"
import Step5_4 from "@/assets/img/astro-map/step5-4.png"
import Step5_5 from "@/assets/img/astro-map/step5-5.png"
import Step7_1 from "@/assets/img/astro-map/step7-1.png"
import Step7_2 from "@/assets/img/astro-map/step7-2.png"
import Step7_3 from "@/assets/img/astro-map/step7-3.png"
import Step7_4 from "@/assets/img/astro-map/step7-4.png"
import Step7_5 from "@/assets/img/astro-map/step7-5.png"
import Step7_6 from "@/assets/img/astro-map/step7-6.png"

import { ANALYTICS_EVENTS } from "@/features/analytics"
import { AstroCoverStep } from "../components/steps/astro-cover-step"
import { AstroMapEmailStep } from "../components/steps/astro-map-email-step"
import { AstroScanningStep } from "../components/steps/astro-scanning-step"
import { AstroSocialProofStep } from "../components/steps/astro-social-proof-step"
import { isFilled, type QuizAnswerValue } from "../components/steps/step-contract"
import { SoulmateChoiceStep } from "../components/steps/soulmate-choice-step"
import { SoulmateInterstitialStep } from "../components/steps/soulmate-interstitial-step"
import { SoulmateMultiChoiceStep } from "../components/steps/soulmate-multi-choice-step"
import type { QuizAnswers, QuizConfig } from "../types"
import { ageFromDob } from "./event-params"

/** Validate that gates Continue on `required` steps (mirrors the shared registry). */
const requiredValidate = (value: QuizAnswerValue | undefined, step: { required?: boolean }) =>
  step.required ? isFilled(value) : true

/** True when the given answer key holds the given value (for branch `showIf`). */
const has = (answers: QuizAnswers, key: string, value: string) => answers[key] === value

/**
 * "Astro Map" quiz (Figma Live-Palmistry-4.0, canvas "Quiz Astro Map", node
 * 2665:20341). Astrocartography funnel: cover → 21 counted questions → map-reveal
 * → scanning/processing → email → discount → subscription paywall (checkout
 * `astro-map`). The email screen sits AFTER the scan so its hero can show the
 * cosmic map the animation just formed.
 *
 * Extends the shared engine, never forks it: most screens are plain config that
 * reuse the Soulmate dumb components via `stepOverrides`; cover/scanning/checkout
 * that structurally diverge get bespoke components. Branch reassurance screens
 * (Step5.x, 7.x, 12.x, 19.x) are gated siblings via `showIf`.
 *
 * WIP: cover + Step1–21 + branches + map-reveal interstitial + email wired.
 * Scanning storyboard, social-proof (Step0.1) and the discount/subscription
 * checkout variant are added next.
 */
export const astroMapQuiz: QuizConfig = {
  id: "astro-map",
  name: "Astro Map",
  steps: [
    // ── Cover (Step0) ────────────────────────────────────────────────────────
    {
      id: "cover",
      eventOnShow: ANALYTICS_EVENTS.ASTROMAP_QUIZ_LOADED,
      type: "cover",
      title: "Find places aligned with you in just 3 minutes",
      subtitle:
        "Discover where your love, career, money and sense of purpose may align strongest, and the places that bring it together.",
      cta: "Let's begin",
      countsTowardProgress: false,
      component: AstroCoverStep,
    },
    // ── Step0.1 — social proof ───────────────────────────────────────────────
    {
      id: "social-proof",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_USERS_TRUST,
      type: "info",
      title: "25 Million+ people have discovered their ideal locations with us",
      cta: "Continue",
      countsTowardProgress: false,
      component: AstroSocialProofStep,
    },
    // ── Step1 — gender (big icons) ───────────────────────────────────────────
    {
      id: "gender",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_1GENDER,
      eventParams: (a) => ({ gender: a.gender }),
      type: "single-choice",
      layout: "icons",
      title: "What's your gender?",
      subtitle: "This helps us shape your readings more accurately",
      required: true,
      options: [
        { label: "Male", value: "male", icon: "mars" },
        { label: "Female", value: "female", icon: "venus" },
        { label: "Other", value: "other", icon: "ellipsis" },
      ],
    },
    // ── Step2 — mood changes by place (list) ─────────────────────────────────
    {
      id: "mood-changes",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_2MOOD,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Does your mood or energy change in different places?",
      required: true,
      options: [
        { label: "Yes, totally", value: "yes", icon: "check", iconColor: "text-cyan" },
        { label: "No, not really", value: "no", icon: "x", iconColor: "text-peach" },
        { label: "I'm not sure", value: "not-sure", icon: "ellipsis" },
      ],
    },
    // ── Step3 — feeling where you live (list) ────────────────────────────────
    {
      id: "current-feeling",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_3FEEL,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How do you usually feel where you currently live?",
      required: true,
      options: [
        { label: "Happy and comfortable", value: "happy" },
        { label: "Fine, but something's missing", value: "fine" },
        { label: "Stuck or uninspired", value: "stuck" },
        { label: "Like I don't belong here at all", value: "dont-belong" },
      ],
    },
    // ── Step4 — "wrong place for my goals" Likert ────────────────────────────
    {
      id: "wrong-place",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_4WRONG_PLACE,
      type: "single-choice",
      layout: "likert",
      bottomDecoration: true,
      title: "I often feel like I'm in the wrong place for my goals",
      subtitle: "How much do you agree with the statement?",
      required: true,
      options: [
        { label: "Strongly disagree", value: "strongly-disagree" },
        { label: "Disagree", value: "disagree" },
        { label: "Neutral", value: "neutral" },
        { label: "Agree", value: "agree" },
        { label: "Strongly agree", value: "strongly-agree" },
      ],
    },
    // ── Step4.x — branch reassurance (one shown, keyed on wrong-place) ────────
    {
      id: "wrong-place-aligned",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_OUTGROWN,
      type: "info",
      title: "You're already aligned with supportive energy",
      subtitle:
        "The comfort you feel where you are matters. Your map may reveal why certain places naturally support your peace, growth, and sense of belonging.",
      coverImage: Step4_1,
      countsTowardProgress: false,
      showIf: (a) =>
        has(a, "wrong-place", "strongly-disagree") || has(a, "wrong-place", "disagree"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "wrong-place-sensing",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_OUTGROWN,
      type: "info",
      title: "You're sensing something important",
      subtitle:
        'That feeling of "something missing" is your intuition speaking. Your map reveals places where you can feel more complete, energized, and aligned.',
      coverImage: Step4_2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "wrong-place", "neutral"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "wrong-place-outgrown",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_OUTGROWN,
      type: "info",
      title: "Your energy may have outgrown this place",
      subtitle:
        "Feeling stuck is often a sign that your environment no longer matches who you're becoming. Your map reveals places where inspiration and momentum can return naturally.",
      coverImage: Step4_3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "wrong-place", "agree"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "wrong-place-far",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_OUTGROWN,
      type: "info",
      title: "You may be far from where you truly belong",
      subtitle:
        "Some places simply don't resonate with your energy. Your map reveals regions where you may finally feel understood, supported, and naturally at home.",
      coverImage: Step4_4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "wrong-place", "strongly-agree"),
      component: SoulmateInterstitialStep,
    },
    // ── Step5 — insight seeking (list, branch source) ────────────────────────
    {
      id: "insight-seeking",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_5INSIGHTS,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What personal insight are you seeking?",
      required: true,
      options: [
        { label: "Where I can grow and feel truly fulfilled", value: "grow-fulfilled" },
        { label: "Where I'm most likely to find real love", value: "find-love" },
        { label: "Where new career opportunities open up", value: "career-opportunities" },
        { label: "Places that feel right for me to explore", value: "explore" },
        { label: "Something else", value: "something-else" },
      ],
    },
    // ── Step5.x — branch reassurance (one shown, keyed on insight-seeking) ────
    {
      id: "insight-grow",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_JOURNEY,
      type: "info",
      title: "You're searching for deeper alignment",
      subtitle:
        "Your map reveals places where life feels more meaningful and emotionally balanced. There, growth feels natural, clarity becomes stronger, and you feel more connected to yourself.",
      coverImage: Step5_1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "insight-seeking", "grow-fulfilled"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "insight-love",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_JOURNEY,
      type: "info",
      title: "Love may feel closer than you think",
      subtitle:
        "Your map reveals places where emotional connection feels more natural and effortless. There, relationships deepen more easily, and meaningful encounters become more likely.",
      coverImage: Step5_2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "insight-seeking", "find-love"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "insight-career",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_JOURNEY,
      type: "info",
      title: "Where opportunities find you",
      subtitle:
        "You'll uncover places where progress feels easier and more natural. There, your skills are recognized, opportunities come to you, and your career starts to reflect your true potential.",
      coverImage: Step5_3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "insight-seeking", "career-opportunities"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "insight-explore",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_JOURNEY,
      type: "info",
      title: "Some places are meant to change you",
      subtitle:
        "Your map reveals destinations that awaken curiosity, movement, and new perspectives. There, exploration feels exciting, inspiring, and deeply personal.",
      coverImage: Step5_4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "insight-seeking", "explore"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "insight-else",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_JOURNEY,
      type: "info",
      title: "Your journey may be leading somewhere important",
      subtitle:
        "Some desires are difficult to explain, but your intuition already feels them. Your map reveals places connected to the deeper energy shaping your path forward.",
      coverImage: Step5_5,
      countsTowardProgress: false,
      showIf: (a) => has(a, "insight-seeking", "something-else"),
      component: SoulmateInterstitialStep,
    },
    // ── Step6 — "felt more alive in certain places" Likert ───────────────────
    {
      id: "alive-energized",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_6FELT_ALIVE,
      type: "single-choice",
      layout: "likert",
      bottomDecoration: true,
      title: "I've felt more alive and energized in certain places I've visited",
      subtitle: "How much do you agree with the statement?",
      required: true,
      options: [
        { label: "Strongly disagree", value: "strongly-disagree" },
        { label: "Disagree", value: "disagree" },
        { label: "Neutral", value: "neutral" },
        { label: "Agree", value: "agree" },
        { label: "Strongly agree", value: "strongly-agree" },
      ],
    },
    // ── Step7 — what made you feel that way (list, branch source) ────────────
    {
      id: "place-feeling-reason",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_7WHAT_MADE_YOU_FEEL,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What made you feel that way?",
      required: true,
      options: [
        { label: "The natural beauty and scenery", value: "natural-beauty" },
        { label: "The energy and pace of the place", value: "energy-pace" },
        { label: "The calmness and peace", value: "calmness-peace" },
        { label: "The people and community", value: "people-community" },
        { label: "Something I can't quite explain", value: "cant-explain" },
        { label: "The opportunities and possibilities", value: "opportunities" },
      ],
    },
    // ── Step7.x — branch reassurance (keyed on place-feeling-reason) ─────────
    {
      id: "reason-beauty",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "You're drawn to natural beauty",
      subtitle:
        "You feel most alive in places filled with beauty, openness, and wonder. In the right environments, your spirit feels peaceful, inspired, and deeply connected.",
      coverImage: Step7_1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "natural-beauty"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "reason-energy",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "You thrive on dynamic energy",
      subtitle:
        "You're energized by places with movement and momentum. In the right environments, the pace feels natural, motivating, engaging, and alive.",
      coverImage: Step7_2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "energy-pace"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "reason-calm",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "Peace and calm fuel your soul",
      subtitle:
        "You feel most alive in places that bring quiet, stillness, and inner balance. In the right environments, your mind clears, your heart softens, and you can truly breathe.",
      coverImage: Step7_3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "calmness-peace"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "reason-people",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "The right people light you up",
      subtitle:
        "Connection, warmth, and community bring out the best in you. In the right environments, you feel understood, supported, and truly at home.",
      coverImage: Step7_4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "people-community"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "reason-magic",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "Some places just feel like magic",
      subtitle:
        "You feel alive in ways you can't always explain. There are places that awaken something deeper within you, beyond logic or words.",
      coverImage: Step7_5,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "cant-explain"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "reason-possibility",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_RIGHT_PEOPLE,
      type: "info",
      title: "You come alive through possibility",
      subtitle:
        "You're energized by places filled with momentum, growth, and opportunity. In the right environments, life feels open, exciting, and full of potential.",
      coverImage: Step7_6,
      countsTowardProgress: false,
      showIf: (a) => has(a, "place-feeling-reason", "opportunities"),
      component: SoulmateInterstitialStep,
    },
    // ── Step8 — what doesn't feel right (list) ───────────────────────────────
    {
      id: "current-place-issue",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_8CURRENT_PLACE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What doesn't feel right about your current place?",
      required: true,
      options: [
        { label: "Limited career opportunities", value: "limited-career" },
        { label: "Difficulty finding meaningful relationships", value: "difficulty-relationships" },
        { label: "Feeling stuck or uninspired", value: "stuck-uninspired" },
        { label: "Financial struggles", value: "financial-struggles" },
        { label: "The environment doesn't suit me", value: "environment-unsuitable" },
        { label: "Nothing, I'm just curious", value: "just-curious" },
      ],
    },
    // ── Step9 — "doesn't support who I want to become" Likert ────────────────
    {
      id: "doesnt-support-growth",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_9LIVE_NOW,
      type: "single-choice",
      layout: "likert",
      bottomDecoration: true,
      title: "Where I live now doesn't fully support who I want to become",
      subtitle: "How much do you agree with the statement?",
      required: true,
      options: [
        { label: "Strongly disagree", value: "strongly-disagree" },
        { label: "Disagree", value: "disagree" },
        { label: "Neutral", value: "neutral" },
        { label: "Agree", value: "agree" },
        { label: "Strongly agree", value: "strongly-agree" },
      ],
    },
    // ── Step10 — what's keeping you from leaving (list) ──────────────────────
    {
      id: "whats-keeping-you",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_10KEEP_FROM_LEAVING,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What's keeping you from leaving?",
      required: true,
      options: [
        { label: "Family and loved ones", value: "family-loved-ones" },
        { label: "My job or career", value: "job-career" },
        { label: "Financial concerns", value: "financial-concerns" },
        { label: "Fear of the unknown", value: "fear-unknown" },
        { label: "Nothing is holding me back", value: "nothing-holding-back" },
        { label: "I'm not sure I want to leave", value: "unsure-leaving" },
      ],
    },
    // ── Step11 — what would make a place perfect (list) ──────────────────────
    {
      id: "perfect-place",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_11PERFECT_PLACE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What would make a place feel perfect for you?",
      required: true,
      options: [
        { label: "Opportunities that align with my goals", value: "aligned-opportunities" },
        { label: "A community where I belong", value: "community-belong" },
        { label: "A place that inspires and energizes me", value: "inspiring-place" },
        { label: "Somewhere that feels like home", value: "feels-like-home" },
        { label: "A fresh start and new possibilities", value: "fresh-start" },
      ],
    },
    // ── Step12 — lifestyle (list, branch source) ─────────────────────────────
    {
      id: "lifestyle",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_12LIFESTYLE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which lifestyle resonates with your soul?",
      required: true,
      options: [
        { label: "Vibrant city energy", value: "vibrant-city-energy" },
        { label: "Peaceful coastal living", value: "peaceful-coastal-living" },
        { label: "Mountain serenity", value: "mountain-serenity" },
        { label: "Nature and countryside", value: "nature-and-countryside" },
        { label: "Nomadic and adventurous", value: "nomadic-and-adventurous" },
        { label: "Cosmopolitan and cultural", value: "cosmopolitan-and-cultural" },
      ],
    },
    // ── Step12.x — branch reassurance (keyed on lifestyle) ───────────────────
    {
      id: "lifestyle-city",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "You come alive in motion",
      subtitle:
        "Fast-moving cities awaken your ambition and curiosity. Your Mercury and Mars lines often cross vibrant urban hubs where inspiration, connection, and momentum constantly surround you.",
      coverImage: Step12_1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "vibrant-city-energy"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "lifestyle-coastal",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "You flow with calm waters",
      subtitle:
        "The ocean restores your emotional balance. Your Moon and Venus lines are often strongest near coastal regions where softness, reflection, and emotional clarity come naturally.",
      coverImage: Step12_2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "peaceful-coastal-living"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "lifestyle-mountain",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "You're drawn to higher ground",
      subtitle:
        "Mountains elevate your spirit. Your Saturn and Uranus lines often cross mountain regions where distance, clarity, and perspective help you see life more clearly and breathe freely.",
      coverImage: Step12_3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "mountain-serenity"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "lifestyle-cultural",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "You thrive where cultures connect",
      subtitle:
        "Creative and multicultural environments stimulate your mind and identity. Your Sun and Mercury lines often align with global cities filled with art, diversity, and meaningful encounters.",
      coverImage: Step12_4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "cosmopolitan-and-cultural"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "lifestyle-nature",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "Your soul seeks simplicity",
      subtitle:
        "Quiet natural spaces help you reconnect with yourself. Your Earth-aligned lines tend to guide you toward peaceful countryside regions where grounding and inner harmony feel effortless.",
      coverImage: Step12_5,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "nature-and-countryside"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "lifestyle-nomadic",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_CALM_WATERS,
      type: "info",
      title: "You're meant to keep exploring",
      subtitle:
        "New places energize your spirit and expand your perspective. Your Jupiter and Uranus lines often encourage movement, discovery, and unexpected opportunities across the world.",
      coverImage: Step12_6,
      countsTowardProgress: false,
      showIf: (a) => has(a, "lifestyle", "nomadic-and-adventurous"),
      component: SoulmateInterstitialStep,
    },
    // ── Step13 — energy source (list) ────────────────────────────────────────
    {
      id: "energy-source",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_13ENERGY,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Where do you get your energy?",
      required: true,
      options: [
        {
          label: "From being around people",
          value: "extrovert",
          icon: "extrovert",
          description: "Extrovert",
        },
        {
          label: "From quiet time alone",
          value: "introvert",
          icon: "introvert",
          description: "Introvert",
        },
        { label: "A balance of both", value: "ambivert", icon: "ambivert", description: "Ambivert" },
      ],
    },
    // ── Step14 — element (list) ──────────────────────────────────────────────
    {
      id: "element",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_14ELEMENT,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which element do you feel most connected to?",
      required: true,
      options: [
        { label: "Fire", value: "fire", icon: "fire", description: "Passionate and driven" },
        { label: "Water", value: "water", icon: "water", description: "Emotional and intuitive" },
        { label: "Earth", value: "earth", icon: "earth", description: "Grounded and practical" },
        { label: "Air", value: "air", icon: "air", description: "Intellectual and free-spirited" },
      ],
    },
    // ── Step15 — relationship status (list) ──────────────────────────────────
    {
      id: "relationship-status",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_15STATUS,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What's your current relationship status?",
      required: true,
      options: [
        { label: "In a relationship", value: "in-a-relationship" },
        { label: "Married", value: "married" },
        { label: "Recently ended a relationship", value: "recently-ended" },
        { label: "Single and open", value: "single-and-open" },
        { label: "Looking for my soulmate", value: "looking-for-soulmate" },
        { label: "It's complicated", value: "its-complicated" },
      ],
    },
    // ── Step16 — career situation (list) ─────────────────────────────────────
    {
      id: "career-situation",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_16STATUS,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What's your career situation?",
      required: true,
      options: [
        { label: "Building my career", value: "building" },
        { label: "Established, seeking growth", value: "established" },
        { label: "Considering a change", value: "considering-change" },
        { label: "Student or in training", value: "student" },
        { label: "Entrepreneur or creative", value: "entrepreneur" },
        { label: "Taking a break", value: "taking-break" },
      ],
    },
    // ── Step17 — "moving could change my life" Likert ────────────────────────
    {
      id: "moving-changes-life",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_17BELIEVE,
      type: "single-choice",
      layout: "likert",
      bottomDecoration: true,
      title: "I believe moving to the right location could change my life",
      subtitle: "How much do you agree with the statement?",
      required: true,
      options: [
        { label: "Strongly disagree", value: "strongly-disagree" },
        { label: "Disagree", value: "disagree" },
        { label: "Neutral", value: "neutral" },
        { label: "Agree", value: "agree" },
        { label: "Strongly agree", value: "strongly-agree" },
      ],
    },
    // ── Step18 — hopes for ideal location (multi-select, up to 3) ────────────
    {
      id: "ideal-location-hopes",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_18IDEAL_LOCATION,
      type: "multiple-choice",
      title: "What are you hoping to find in your ideal location?",
      subtitle: "Select up to 3",
      required: true,
      maxSelect: 3,
      options: [
        { label: "Career advancement", value: "career-advancement" },
        { label: "Romantic connection", value: "romantic-connection" },
        { label: "Family harmony", value: "family-harmony" },
        { label: "Financial prosperity", value: "financial-prosperity" },
        { label: "Creative inspiration", value: "creative-inspiration" },
        { label: "Spiritual growth", value: "spiritual-growth" },
        { label: "Inner peace", value: "inner-peace" },
        { label: "Peaceful lifestyle", value: "peaceful-lifestyle" },
      ],
    },
    // ── Step19 — meaning of "home" (list, branch source) ─────────────────────
    {
      id: "home-meaning",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_19HOME,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: 'How do you feel about the concept of "home"?',
      required: true,
      options: [
        { label: "Home is where I grew up", value: "grew-up" },
        { label: "Home is where my loved ones are", value: "loved-ones" },
        { label: "Home is wherever I feel at peace", value: "at-peace" },
        { label: "Home is something I'm still searching for", value: "searching" },
        { label: "Home is within me", value: "within-me" },
      ],
    },
    // ── Step19.x — branch reassurance (keyed on home-meaning) ────────────────
    {
      id: "home-roots",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_ANCHOR,
      type: "info",
      title: "Your roots still shape your path",
      subtitle:
        "The places connected to your past hold deep emotional energy for you. Your map highlights regions where familiarity, grounding, and belonging feel natural.",
      coverImage: Step19_1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "home-meaning", "grew-up"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "home-love",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_ANCHOR,
      type: "info",
      title: "Love is your anchor",
      subtitle:
        "The people you care about shape what “home” means to you. Your map highlights places where those connections feel supported, where bonds deepen and a shared sense of belonging can grow.",
      coverImage: Step19_2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "home-meaning", "loved-ones"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "home-peace",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_ANCHOR,
      type: "info",
      title: "Peace is what guides you home",
      subtitle:
        "You feel most connected in places that bring calm, balance, and emotional clarity. Your map reveals regions where your soul feels lighter, quieter, and deeply aligned.",
      coverImage: Step19_3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "home-meaning", "at-peace"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "home-searching",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_ANCHOR,
      type: "info",
      title: "You may still be searching for where you belong",
      subtitle:
        "Not everyone finds the feeling of home right away. Your map reveals places that may awaken comfort, connection, and a deeper sense of belonging.",
      coverImage: Step19_4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "home-meaning", "searching"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "home-within",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_ANCHOR,
      type: "info",
      title: "Your sense of home comes from within",
      subtitle:
        "You carry your emotional center wherever you go. Your map highlights places that support your independence, inner balance, and true sense of self.",
      coverImage: Step19_5,
      countsTowardProgress: false,
      showIf: (a) => has(a, "home-meaning", "within-me"),
      component: SoulmateInterstitialStep,
    },
    // ── Step20 — date of birth (wheel picker) ────────────────────────────────
    {
      id: "dob",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_20BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a) }),
      type: "date",
      dateMode: "dob",
      title: "What's your date of birth?",
      subtitle: "We'll use this to calculate your zodiac and birth chart",
      required: true,
    },
    // ── Step21 — place of birth (location input) ─────────────────────────────
    {
      id: "birthplace",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_21BIRTHPLACE,
      type: "input-city",
      title: "Where were you born?",
      subtitle: "This sets the foundation for your map",
      placeholder: "Place of birth",
      required: true,
      reassurance: "We don't share any personal information",
    },
    // ── Map-reveal interstitial (pre-email teaser) ───────────────────────────
    {
      id: "map-reveal",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_MAP,
      type: "info",
      title: "Your map is starting to reveal itself",
      subtitle:
        "We've identified the key energies influencing your path. When your map is revealed, the Sun and Jupiter lines highlight the energies shaping your path based on what we know about you.",
      coverImage: MapReveal,
      countsTowardProgress: false,
      component: SoulmateInterstitialStep,
    },
    // ── Scanning / processing storyboard ─────────────────────────────────────
    {
      id: "scanning",
      eventOnShow: ANALYTICS_EVENTS.ASTROMAP_QUIZ_PROCESSING,
      type: "analysis",
      title: "Preparing your cosmic location guide...",
      countsTowardProgress: false,
    },
    // ── Email ─ shown AFTER the scan, hero is the map the animation just formed ─
    {
      id: "email",
      event: ANALYTICS_EVENTS.ASTROMAP_QUIZ_EMAIL,
      type: "email",
      title: "Are you ready to reveal your cosmic map?",
      subtitle:
        "Enter your email to receive your personalized insights and see the places aligned with your energy.",
      placeholder: "Enter your email",
      required: true,
      legalNote: true,
      submitDelayMs: 1200,
      countsTowardProgress: false,
      component: AstroMapEmailStep,
    },
  ],
  stepOverrides: {
    // Reuse the Soulmate dumb components — the rows/scales diverge from the shared
    // choice steps identically across both funnels. The global registry and the
    // other quizzes stay untouched.
    "single-choice": {
      autoAdvance: true,
      validate: requiredValidate,
      Component: SoulmateChoiceStep,
    },
    "multiple-choice": {
      validate: requiredValidate,
      Component: SoulmateMultiChoiceStep,
    },
    analysis: {
      fullBleed: true,
      validate: () => true,
      Component: AstroScanningStep,
    },
  },
}
